import dayjs from "dayjs";
import Cookies from "js-cookie";
import { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import Colors from "@constants/Colors";
import { dashboards } from "@constants/Dashboard";
import ErrorToast from "@constants/ErrorToast";
import SuccessToast from "@constants/SuccessToast";
import authContext from "@context/Auth/authContext";
import LocationContext from "@context/location/locationContext";
import ShowErrorToastContext from "@context/showErrorToast/showErrorToastContext";
import { DEVICE, useWindowSizeChanged } from "@hooks/DeviceSize";
import MenuIcon from "@icons/menu.svg";
import { Container, Grid } from "@mui/material";
import DateSelectorDialog from "@src/components/shared-containers/DateSelectorDialog";
import LocationSelectorDialog from "@src/components/shared-containers/LocationSelectorDialog";
import Header from "@shared-layouts/Header";
import SideBar from "@shared-layouts/Sidebar";
import { todayDate } from "@src/utils/checkAndUpdateDate";
import {
  blacklist,
  ConditionalRoutes,
  DEFAULT_MIN_DATE,
  HistoricalReportRoute,
  isProgressiveWebApp,
  localTableFilters,
  message,
  PropertyValues,
  scrollToTop,
} from "@src/constants";

import Loader from "@shared-containers/Loader";
import PullToRefresh from "react-simple-pull-to-refresh";
import HrNavBar from "@shared-containers/HistoricalReportsContainer/HRNavBar";
import { clearCookies } from "@utils/clearCookies";
import useClearContext from "@hooks/useClearContext";
import SignInPage from "../SignInPage";
import DateLocationButton from "./DateLocationButton";
import LogOutModal from "./LogOutModal";

import { StyledContainer, StyledDrawer } from "./styles";

const DefaultLayout = () => {
  const navigate = useNavigate();

  const [{ state }] = useContext(authContext);
  const [{ locationState, locationSetState }] = useContext(LocationContext);
  const [{ showErrorToastSetState }] = useContext(ShowErrorToastContext);
  const { revenueCenter } = locationState;

  const [property, setProperty] = useState<string>("");
  const [date, setDate] = useState<string>(todayDate);
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [locationOpen, setLocationOpen] = useState<boolean>(false);
  const [dateOpen, setDateOpen] = useState<boolean>(false);
  const [logOutOpen, setLogoutOpen] = useState<boolean>(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const device = useWindowSizeChanged();
  const isDesktop = device === DEVICE.DESKTOP;
  const { pathname } = useLocation();
  const pageheader = pathname.split("/")[1].split("-")[0];
  const heading = dashboards[pageheader]?.toLowerCase().includes(pageheader)
    ? dashboards[pageheader]
    : "Home Dashboard";

  const locationString = localStorage.getItem("locationDetails");
  const locationidsString = localStorage.getItem("locationIds");
  const dateString = localStorage.getItem("date");
  const formatteddate = dayjs(date).format("MMM DD, YYYY");
  const gridSize = isDesktop ? 9 : 12;

  const userData = Cookies.get("userData") || "";
  const parsedUserData = userData ? JSON.parse(userData) : null;
  const userToken: string | undefined = parsedUserData?.access_token;

  const currentLocationIds = () => {
    let ids;
    if (locationidsString !== null) {
      ids = JSON.parse(locationidsString);
    }
    return ids;
  };

  const currentLocation = () => {
    let location;
    if (locationString !== null) {
      location = JSON.parse(locationString);
    }
    return location;
  };

  useEffect(() => {
    if (!userToken) {
      // Clear state when user is not authenticated
      localStorage.removeItem("locationDetails");
      localStorage.removeItem("locationIds");
      localStorage.removeItem("date");
      setProperty("");
      setDate(todayDate);
      setHasInitialized(false);
      navigate("/login");
    }
  }, [state?.token, userToken, navigate]);

  useEffect(() => {
    if (!hasInitialized) {
      setIsInitializing(true);

      // Check if we have a valid token before using stored state
      if (userToken && locationString !== null) {
        const localLocation = () => {
          const { store, propertyShortname } = JSON.parse(locationString);
          if (
            JSON.parse(locationString)?.property?.length > 0 &&
            store?.length <= 0
          ) {
            return JSON.parse(locationString)?.property;
          }
          if (
            JSON.parse(locationString)?.property?.length > 0 &&
            store?.length > 0
          ) {
            return `${propertyShortname} ${store}`;
          }
          return JSON.parse(locationString)?.property;
        };
        setProperty(localLocation());
      } else if (!userToken) {
        // Clear state if no valid token
        setProperty("");
      }

      if (userToken && dateString !== null) {
        setDate(JSON.parse(dateString));
      } else if (!userToken) {
        // Reset to today's date if no valid token
        setDate(todayDate);
      }

      if (userToken && locationidsString !== null && dateString !== null) {
        const location = JSON.parse(locationidsString);
        const localLocationIds = {
          ...locationState,
          property: location.property,
          store: location.store_id,
          revenueCenter: location.rvc_id,
          date: JSON.parse(dateString),
        };
        locationSetState(localLocationIds);
      } else if (!userToken) {
        // Reset location state if no valid token
        locationSetState({
          property: "",
          store: "",
          revenueCenter: "",
          date: todayDate,
        });
      }

      setHasInitialized(true);
      setIsInitializing(false);
    }
    scrollToTop();
  }, [locationState, hasInitialized, userToken]);

  useEffect(() => {
    scrollToTop();
  }, [dateOpen, locationOpen, pathname]);

  useEffect(() => {
    if (blacklist.some((item) => pathname?.includes(item))) return;
    localTableFilters.forEach((el) => {
      localStorage.removeItem(el);
    });
  }, [pathname]);

  useEffect(() => {
    if (
      Object.values(ConditionalRoutes).some((item) => pathname?.includes(item))
    ) {
      Object.values(PropertyValues).forEach((el) => {
        const RcPinnacle = locationState?.property === el;
        const QSProperty = el !== locationState?.property;
        const isLabourDashboard = pathname?.includes(
          ConditionalRoutes.LabourDashboard
        );
        if (
          (RcPinnacle && revenueCenter) ||
          (QSProperty && isLabourDashboard && revenueCenter)
        ) {
          navigate("/");
          showErrorToastSetState({
            errorMessage: message.ERROR_MESSAGE_UNAUTHORIZED,
            errorTitle: message.ERROR_MESSAGE,
            open: true,
          });
        }
      });
    }
  }, [pathname, revenueCenter]);

  const handleOpenLogOutModal = () => {
    setLogoutOpen(true);
  };

  const onHandleCloseLogout = () => {
    setLogoutOpen(false);
  };

  const onHandleLogout = () => {
    setLogoutOpen(false);
    localStorage.clear();
    clearCookies();
    useClearContext();
    // Reset local state as well
    setProperty("");
    setDate(todayDate);
    setHasInitialized(false);
  };

  const handleClick = () => setMobileOpen(!mobileOpen);

  const handleChangeLocationClick = () => {
    setDateOpen(false);
    setLocationOpen(true);
  };

  const handleChangeDateClick = () => {
    setLocationOpen(false);
    setDateOpen(true);
  };

  const handleLocationClose = (selectedLocation?: string) => {
    setProperty(selectedLocation || property);
    setLocationOpen(false);
  };

  const handleDateClose = (selectedDate: string) => {
    setDate(selectedDate);
    setDateOpen(false);
  };

  if (!userToken) {
    return <SignInPage />;
  }

  const refreshHandler = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate(0);
        resolve("reload");
      }, 2000);
    });

  return (
    <>
      <StyledContainer>
        <Header
          icon={MenuIcon}
          heading={heading}
          handleClick={handleClick}
          handleChangeDateClick={handleChangeDateClick}
          formatteddate={formatteddate}
          handleChangeLocationClick={handleChangeLocationClick}
          property={property}
          showLogo
        />
        <Grid direction="row" container>
          <Grid xs={3} item>
            {!isDesktop ? (
              <StyledDrawer
                variant="temporary"
                ModalProps={{
                  BackdropProps: {
                    style: { backgroundColor: "transparent" },
                  },
                }}
                open={mobileOpen}
                onClose={handleClick}
              >
                <SideBar
                  handleOpenLogOutModal={handleOpenLogOutModal}
                  handleCloseDrawer={handleClick}
                />
              </StyledDrawer>
            ) : (
              <SideBar
                handleOpenLogOutModal={handleOpenLogOutModal}
                handleCloseDrawer={handleClick}
              />
            )}
          </Grid>
          <Grid
            xs={gridSize}
            sx={{
              backgroundColor: Colors.neutralwhite_50,
            }}
            item
          >
            <Container disableGutters>
              {!isDesktop && (
                <DateLocationButton
                  handleChangeDateClick={handleChangeDateClick}
                  formatteddate={formatteddate}
                  handleChangeLocationClick={handleChangeLocationClick}
                  property={property}
                  isDesktop={isDesktop}
                />
              )}
              {pathname?.includes(HistoricalReportRoute) && (
                <HrNavBar isDesktop={isDesktop} />
              )}
              <PullToRefresh
                onRefresh={refreshHandler}
                isPullable={isProgressiveWebApp}
              >
                {isInitializing ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "50vh",
                    }}
                  >
                    <Loader />
                  </div>
                ) : (
                  <Outlet />
                )}
              </PullToRefresh>
            </Container>
          </Grid>
        </Grid>
        <ErrorToast />
        <SuccessToast />
        <LocationSelectorDialog
          currentLocationIds={currentLocationIds()}
          currentLocation={currentLocation()}
          open={locationOpen}
          onClose={handleLocationClose}
        />
        <DateSelectorDialog
          minDate={DEFAULT_MIN_DATE}
          date={date}
          open={dateOpen}
          onClose={handleDateClose}
        />
      </StyledContainer>
      {logOutOpen && (
        <LogOutModal
          open={logOutOpen}
          onHandleLogout={onHandleLogout}
          onHandleClose={onHandleCloseLogout}
        />
      )}
    </>
  );
};

export default DefaultLayout;
